import { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Plus, Trash2, TrendingUp } from "lucide-react";

export default function NetWorth() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [income, setIncome] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await api.get("/networth");
    setEntries(data.entries);
    setTotal(data.total);
  };
  useEffect(() => { load(); }, []);

  const persist = async (newEntries) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/networth", { entries: newEntries });
      setEntries(data.entries);
      setTotal(data.total);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const addEntry = (e) => {
    e.preventDefault();
    const inc = parseFloat(income);
    if (!year || isNaN(inc)) return;
    const next = [...entries, { year: parseInt(year), income: inc, label }];
    setIncome(""); setLabel("");
    persist(next);
  };

  const removeEntry = (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    persist(next);
  };

  const years = entries.length;
  const avg = years > 0 ? total / years : 0;

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Lifetime Earnings</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Everything you&apos;ve ever earned.</h1>
        <p className="text-moss mt-3 max-w-2xl leading-relaxed">
          Add a line for each year you&apos;ve worked since your first job. Your estimated lifetime earnings are the
          combined gross income across all those years.
        </p>

        <div className="grid lg:grid-cols-12 gap-8 mt-12">
          {/* Total */}
          <div className="lg:col-span-5 bg-ink text-cream rounded-2xl p-8 h-fit" data-testid="networth-summary">
            <TrendingUp className="w-6 h-6 text-cream/80" strokeWidth={1.5} />
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mt-6">Estimated lifetime earnings</div>
            <div className="font-mono text-5xl mt-2" data-testid="networth-total">{fmtMoney(total)}</div>
            <div className="grid grid-cols-2 gap-6 mt-10 pt-6 border-t border-white/10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Years tracked</div>
                <div className="font-mono text-2xl mt-1">{years}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Avg / year</div>
                <div className="font-mono text-2xl mt-1">{fmtMoney(avg)}</div>
              </div>
            </div>
          </div>

          {/* Add + list */}
          <div className="lg:col-span-7">
            <form onSubmit={addEntry} className="bg-linen border border-sand rounded-2xl p-6 mb-6" data-testid="add-year-form">
              <div className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-3">
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Year</label>
                  <input type="number" required value={year} onChange={(e) => setYear(e.target.value)}
                    min="1950" max={new Date().getFullYear()}
                    className="mt-2 w-full bg-cream border border-sand rounded-lg px-3 py-2.5 font-mono focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                    data-testid="year-input" />
                </div>
                <div className="col-span-4">
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Income (USD)</label>
                  <input type="number" step="0.01" min="0" required value={income} onChange={(e) => setIncome(e.target.value)}
                    placeholder="45000"
                    className="mt-2 w-full bg-cream border border-sand rounded-lg px-3 py-2.5 font-mono focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                    data-testid="income-input" />
                </div>
                <div className="col-span-3">
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Note</label>
                  <input type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                    placeholder="First job"
                    className="mt-2 w-full bg-cream border border-sand rounded-lg px-3 py-2.5 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                    data-testid="label-input" />
                </div>
                <button type="submit" disabled={busy}
                  className="col-span-2 bg-forest text-white rounded-lg px-4 py-2.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors inline-flex items-center justify-center gap-1.5"
                  data-testid="add-year-btn">
                  <Plus className="w-4 h-4" strokeWidth={2} /> Add
                </button>
              </div>
              {error && <div className="mt-3 text-sm text-clay" data-testid="networth-error">{error}</div>}
            </form>

            {entries.length === 0 ? (
              <div className="border border-dashed border-sand rounded-2xl p-12 text-center text-sage" data-testid="networth-empty">
                No years added yet. Add your first above.
              </div>
            ) : (
              <div className="bg-linen border border-sand rounded-2xl overflow-hidden" data-testid="networth-list">
                <div className="grid grid-cols-12 px-6 py-3 text-xs uppercase tracking-[0.2em] font-semibold text-sage">
                  <div className="col-span-2">Year</div>
                  <div className="col-span-6">Note</div>
                  <div className="col-span-3 text-right">Income</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="divide-y divide-sand">
                  {entries.map((e, idx) => (
                    <div key={`${e.year}-${idx}`} className="grid grid-cols-12 px-6 py-3 items-center text-sm" data-testid={`year-row-${e.year}`}>
                      <div className="col-span-2 font-mono text-ink">{e.year}</div>
                      <div className="col-span-6 text-moss">{e.label || "—"}</div>
                      <div className="col-span-3 text-right font-mono text-ink">{fmtMoney(e.income)}</div>
                      <div className="col-span-1 flex justify-end">
                        <button onClick={() => removeEntry(idx)} className="text-moss hover:text-clay" aria-label="Remove" data-testid={`remove-year-${idx}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
